import { Redirect, Route, Switch } from 'react-router-dom';
import { WATCHLIST_ENABLED } from '@/const/features';
import useScrollToTop from '@/hooks/useScrollToTop';
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout';
import MainLayout from '@/layouts/MainLayout';
import UnauthorizedLayout from '@/layouts/UnauthorizedLayout';
import { retryLazyLoad } from '@/utils/retryLazyLoads';
import RequireAuth from './RequireAuth';
import { ROUTES } from './routes';

// NotFound
const NotFound = retryLazyLoad(() => {
    return import('@/pages/NotFound');
});

// Auth
const Login = retryLazyLoad(() => {
    return import('@/pages/Auth/Login');
});

const LoginByQR = retryLazyLoad(() => {
    return import('@/pages/Auth/LoginByQR');
});

const SuccessfulPair = retryLazyLoad(() => {
    return import('@/pages/Auth/SuccessfulPair');
});

// Home
const Home = retryLazyLoad(() => {
    return import('@/pages/Home');
});

// Play
const Play = retryLazyLoad(() => {
    return import('@/pages/Play');
});

// Movies
const Movies = retryLazyLoad(() => {
    return import('@/pages/Medias/Movies');
});

const MovieDetail = retryLazyLoad(() => {
    return import('@/pages/Medias/Movies/MovieDetail');
});

// Series
const Series = retryLazyLoad(() => {
    return import('@/pages/Medias/Series');
});

const SeriesDetail = retryLazyLoad(() => {
    return import('@/pages/Medias/Series/SeriesDetail');
});

// Search
const Search = retryLazyLoad(() => {
    return import('@/pages/Medias/Search');
});

// Live
const Live = retryLazyLoad(() => {
    return import('@/pages/Medias/Live');
});

// YouTube Persian
const YoutubePersian = retryLazyLoad(() => {
    return import('@/pages/YoutubePersian');
});

const YoutubeChannel = retryLazyLoad(() => {
    return import('@/pages/YoutubePersian/Channel');
});

// Info
// const Maintenance = retryLazyLoad(() => {
//     return import('@/pages/Infos/Maintenance');
// });
const Disclaimer = retryLazyLoad(() => {
    return import('@/pages/Infos/Disclaimer');
});
const Policy = retryLazyLoad(() => {
    return import('@/pages/Infos/Policy');
});
const Terms = retryLazyLoad(() => {
    return import('@/pages/Infos/Terms');
});
const DeleteAccount = retryLazyLoad(() => {
    return import('@/pages/Infos/DeleteAccount');
});
// Media List
const MediaList = retryLazyLoad(() => {
    return import('@/pages/Medias/MediaList');
});

const Watchlist = retryLazyLoad(() => import('@/pages/Medias/Watchlist'));

export const Router = () => {
    useScrollToTop();
    return (
        <Switch>
            {/* <Route path="*">
                <Maintenance />
            </Route> */}
            {/* Public Routes */}
            <Route path={ROUTES.AUTH.LOGIN} exact>
                <UnauthorizedLayout>
                    <Login />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.AUTH.LOGIN_WITH_QR} exact>
                <UnauthorizedLayout>
                    <LoginByQR />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.AUTH.PAIR} exact>
                <UnauthorizedLayout>
                    <Login />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.AUTH.SUCCESSFUL_PAIR} exact>
                <UnauthorizedLayout>
                    <SuccessfulPair />
                </UnauthorizedLayout>
            </Route>

            <Route path={ROUTES.INFO.DISCLAIMER} exact>
                <UnauthorizedLayout>
                    <Disclaimer />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.INFO.TERMS_OF_SERVICE} exact>
                <UnauthorizedLayout>
                    <Terms />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.INFO.PRIVACY_POLICY} exact>
                <UnauthorizedLayout>
                    <Policy />
                </UnauthorizedLayout>
            </Route>
            <Route path={ROUTES.INFO.DELETE_ACCOUNT} exact>
                <UnauthorizedLayout>
                    <DeleteAccount />
                </UnauthorizedLayout>
            </Route>

            {/* Play Route - chrome-less fullscreen player, auth-guarded synchronously */}
            <Route path={ROUTES.MEDIAS.PLAY} exact>
                <RequireAuth>
                    <Play />
                </RequireAuth>
            </Route>

            {/* Protected Routes */}
            <Route path="/">
                <MainLayout>
                    <Switch>
                        <Route path={ROUTES.HOME} exact>
                            <AuthenticatedLayout>
                                <Home />
                            </AuthenticatedLayout>
                        </Route>

                        <Route path={ROUTES.MEDIAS.WATCHLIST} exact>
                            {WATCHLIST_ENABLED ? (
                                <RequireAuth>
                                    <AuthenticatedLayout>
                                        <Watchlist />
                                    </AuthenticatedLayout>
                                </RequireAuth>
                            ) : (
                                <Redirect to={ROUTES.MEDIAS.MOVIES} />
                            )}
                        </Route>

                        {/* Media Routes */}
                        <Route path={ROUTES.MEDIAS.MOVIES} exact>
                            <AuthenticatedLayout>
                                <Movies />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.MEDIAS.MOVIE_DETAIL} exact>
                            <AuthenticatedLayout>
                                <MovieDetail />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.MEDIAS.SERIES} exact>
                            <AuthenticatedLayout>
                                <Series />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.MEDIAS.SERIES_DETAIL} exact>
                            <AuthenticatedLayout>
                                <SeriesDetail />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.MEDIAS.SEARCH} exact>
                            <AuthenticatedLayout>
                                <Search />
                            </AuthenticatedLayout>
                        </Route>

                        <Route path={ROUTES.MEDIAS.LIST} exact>
                            <AuthenticatedLayout>
                                <MediaList />
                            </AuthenticatedLayout>
                        </Route>

                        {/* Live Route  */}
                        <Route path={ROUTES.MEDIAS.LIVE} exact>
                            <AuthenticatedLayout>
                                <Live />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.MEDIAS.LIVE_CHANNEL} exact>
                            <AuthenticatedLayout>
                                <Live />
                            </AuthenticatedLayout>
                        </Route>

                        {/* YouTube Persian Routes */}
                        <Route path={ROUTES.YOUTUBE_PERSIAN.LIST} exact>
                            <AuthenticatedLayout>
                                <YoutubePersian />
                            </AuthenticatedLayout>
                        </Route>
                        <Route path={ROUTES.YOUTUBE_PERSIAN.CHANNEL} exact>
                            <AuthenticatedLayout>
                                <YoutubeChannel />
                            </AuthenticatedLayout>
                        </Route>

                        {/* 404 Route */}
                        <Route path="*" component={NotFound} />
                    </Switch>
                </MainLayout>
            </Route>
        </Switch>
    );
};
